import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { submitMyPost, myPosts, aiDraft } from "../lib/api";
import { Toolbar, Field, Card, inputCls } from "./AdminLayout";
import { MessageSquareQuote, Trophy, PenLine, Clock, CheckCircle2 } from "lucide-react";
import MicButton from "../components/MicButton";
import ImageUpload from "../components/ImageUpload";
import { fmtEventDate } from "../lib/eventFormat";

const SPORTS = ["Football", "Basketball", "Baseball", "Softball", "Volleyball", "Soccer", "Track & Field", "Swimming", "Other"];

export default function SubmitAdmin() {
  const [kind, setKind] = useState("voice");
  const [form, setForm] = useState(blank());
  const [notes, setNotes] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  function blank() { return { title: "", image: "", sport: "Football", home_team: "", away_team: "", home_score: "", away_score: "", standouts: "" }; }
  const load = () => myPosts().then(setMine).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    if (!notes.trim()) return toast.error("Dictate or type some notes first.");
    setAiBusy(true);
    try {
      const d = await aiDraft({ notes, category: kind === "score" ? "sports" : "community" });
      setForm((f) => ({ ...f, title: d.title || f.title }));
      setBodyText((d.body || []).join("\n\n") || bodyText);
      toast.success("Draft cleaned up — review before submitting.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not tidy up the draft. Please try again."); }
    finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("A headline is required.");
    const body = bodyText.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    const payload = { kind, title: form.title, body, image: form.image,
      sport: kind === "score" ? form.sport : "", home_team: kind === "score" ? form.home_team : "",
      away_team: kind === "score" ? form.away_team : "", home_score: kind === "score" ? form.home_score : "",
      away_score: kind === "score" ? form.away_score : "", standouts: kind === "score" ? form.standouts : "" };
    setBusy(true);
    try {
      await submitMyPost(payload);
      toast.success("Submitted! A sports editor will review it before it goes live.");
      setForm(blank()); setNotes(""); setBodyText(""); load();
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Toolbar title="Submit a Post" subtitle="Share a community voice or file a game report — an editor approves before it goes live" />
      <div className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={submit} data-testid="submit-post-form" className="lg:col-span-2">
          <Card className="p-5">
            <div className="mb-4 flex gap-2">
              <KindBtn active={kind === "voice"} onClick={() => setKind("voice")} icon={MessageSquareQuote} testid="kind-voice">Community Voice</KindBtn>
              <KindBtn active={kind === "score"} onClick={() => setKind("score")} icon={Trophy} testid="kind-score">Game Report</KindBtn>
            </div>

            <div className="rounded-xl border border-[var(--gold)]/50 bg-amber-50/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 font-semibold text-[var(--navy)]"><PenLine size={16} className="text-[var(--gold)]" /> {kind === "score" ? "Talk through the game" : "Dictate your piece"}</p>
                <MicButton onText={(t) => setNotes((n) => (n ? n + " " : "") + t)} testid="submit-mic" />
              </div>
              <textarea data-testid="submit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                placeholder={kind === "score" ? "Right after the game, tap Dictate and recap what happened — key plays, momentum, standout players. We'll tidy it up." : "Tap Dictate and share your thoughts, or type them here."}
                className={`${inputCls} mt-3`} />
              <button type="button" onClick={generate} disabled={aiBusy} data-testid="submit-ai" className="mt-2 flex items-center gap-2 rounded-lg bg-[var(--navy)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[var(--header-navy)] disabled:opacity-60">
                <PenLine size={14} /> {aiBusy ? "Tidying up…" : "Tidy it up → fills the post below"}
              </button>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Headline" full><input data-testid="sp-title" value={form.title} onChange={set("title")} className={inputCls} placeholder={kind === "score" ? "Pirates edge Yulee in overtime" : "Why I love our farmers market"} /></Field>
              {kind === "score" && (
                <>
                  <Field label="Sport"><select data-testid="sp-sport" value={form.sport} onChange={set("sport")} className={inputCls}>{SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                  <Field label="Standout players / stats"><input data-testid="sp-standouts" value={form.standouts} onChange={set("standouts")} className={inputCls} placeholder="QB Jalen Carter — 3 TDs" /></Field>
                  <Field label="Home team"><input data-testid="sp-home" value={form.home_team} onChange={set("home_team")} className={inputCls} /></Field>
                  <Field label="Home score"><input data-testid="sp-home-score" value={form.home_score} onChange={set("home_score")} className={inputCls} /></Field>
                  <Field label="Away team"><input data-testid="sp-away" value={form.away_team} onChange={set("away_team")} className={inputCls} /></Field>
                  <Field label="Away score"><input data-testid="sp-away-score" value={form.away_score} onChange={set("away_score")} className={inputCls} /></Field>
                </>
              )}
              <Field label={kind === "score" ? "Game recap" : "Your post"} full>
                <textarea data-testid="sp-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={5} className={inputCls} />
              </Field>
              <Field label="Photo (optional)" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="sp-image" /></Field>
            </div>
            <div className="mt-4 flex justify-end">
              <button type="submit" disabled={busy} data-testid="sp-submit" className="rounded-lg bg-[var(--gold)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#b08f48] disabled:opacity-60">
                {busy ? "Submitting…" : "Submit for approval"}
              </button>
            </div>
          </Card>
        </form>

        <div>
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">My submissions</h3>
          <div className="space-y-3" data-testid="my-submissions">
            {mine.length === 0 && <p className="text-sm text-slate-400">Nothing submitted yet.</p>}
            {mine.map((p) => (
              <Card key={p.id} data-testid={`my-post-${p.id}`} className="p-3">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{p.kind === "score" ? "Game Report" : "Voice"}</span>
                  {p.status === "published"
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><CheckCircle2 size={12} /> Live</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600"><Clock size={12} /> Pending</span>}
                </div>
                <p className="mt-1 text-sm font-semibold text-[var(--navy)] line-clamp-2">{p.title}</p>
                <p className="text-[11px] text-slate-400">{fmtEventDate(p.created_at)}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const KindBtn = ({ active, onClick, icon: Icon, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-[var(--navy)] text-white" : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
    <Icon size={16} /> {children}
  </button>
);
